import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@/shared/icons';
import { createPortal } from 'react-dom';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';

/**
 * Interface para usuário
 */
export interface User {
  id: string;
  name: string;
  avatar?: string;
}

/**
 * Props do UsersMultiSelect
 */
export interface UsersMultiSelectProps {
  /** Lista de usuários disponíveis */
  users: User[];
  /** IDs dos usuários selecionados */
  selectedUserIds: string[];
  /** Callback quando seleção muda */
  onChange: (userIds: string[]) => void;
  /** Placeholder do botão */
  placeholder?: string;
  /** Tamanho máximo de avatares visíveis */
  maxVisible?: number;
  /** Estado de loading */
  loading?: boolean;
}

/**
 * Componente de seleção múltipla de usuários
 *
 * Exibe avatares dos usuários selecionados e permite adicionar/remover
 * através de um dropdown.
 *
 * @example
 * ```tsx
 * <UsersMultiSelect
 *   users={allUsers}
 *   selectedUserIds={selectedIds}
 *   onChange={setSelectedIds}
 *   placeholder="Vincular usuários"
 * />
 * ```
 */
export const UsersMultiSelect: React.FC<UsersMultiSelectProps> = ({
  users,
  selectedUserIds,
  onChange,
  placeholder = 'Vincular usuários',
  maxVisible = 3,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  const handleToggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onChange(selectedUserIds.filter(id => id !== userId));
    } else {
      onChange([...selectedUserIds, userId]);
    }
  };

  const handleRemoveUser = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedUserIds.filter(id => id !== userId));
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full animate-pulse"
          style={{ backgroundColor: 'var(--theme-border)' }}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Button com avatares selecionados */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors"
        style={{
          backgroundColor: '#ffffff',
          borderColor: '#d1d5db',
          color: '#1e293b'
        }}
      >
        <Icon name="users" className="w-4 h-4" style={{ color: '#64748b' }} />

        {selectedUsers.length === 0 ? (
          <span style={{ color: '#9ca3af' }}>{placeholder}</span>
        ) : (
          <>
            <div className="flex items-center -space-x-2">
              {selectedUsers.slice(0, maxVisible).map((user) => (
                <div
                  key={user.id}
                  className="relative group"
                  title={user.name}
                >
                  <Avatar className="w-6 h-6 border-2 border-white" isSquircle>
                    <AvatarImage src={user.avatar} alt={user.name} isSquircle />
                    <AvatarFallback isSquircle>
                      <span className="text-[10px] font-medium">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={(e) => handleRemoveUser(user.id, e)}
                    className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
                  >
                    <Icon name="x" className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
            </div>
            {selectedUsers.length > maxVisible && (
              <span className="text-xs" style={{ color: '#64748b' }}>
                +{selectedUsers.length - maxVisible}
              </span>
            )}
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[100]"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown content */}
          <div
            className="fixed w-72 rounded-md shadow-lg z-[101] max-h-80 flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
            }}
          >
            {/* Search */}
            <div className="p-3 border-b" style={{ borderColor: '#e5e7eb' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar usuário..."
                className="w-full px-3 py-1.5 text-sm rounded border"
                style={{
                  backgroundColor: '#f9fafb',
                  borderColor: '#d1d5db',
                  color: '#1e293b'
                }}
                autoFocus
              />
            </div>

            {/* Users list */}
            <div className="overflow-y-auto flex-1 p-2">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-4 text-sm" style={{ color: '#9ca3af' }}>
                  Nenhum usuário encontrado
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleToggleUser(user.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors text-left"
                        style={{
                          backgroundColor: isSelected ? '#f3f4f6' : 'transparent',
                          color: '#1e293b'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <Avatar className="w-8 h-8" isSquircle>
                          <AvatarImage src={user.avatar} alt={user.name} isSquircle />
                          <AvatarFallback isSquircle>
                            <span className="text-xs font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </AvatarFallback>
                        </Avatar>
                        <span className="flex-1">{user.name}</span>
                        {isSelected && (
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: '#8b5cf6' }}
                          >
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer com contador */}
            {selectedUsers.length > 0 && (
              <div
                className="px-3 py-2 border-t text-xs"
                style={{
                  borderColor: '#e5e7eb',
                  color: '#64748b'
                }}
              >
                {selectedUsers.length} usuário{selectedUsers.length !== 1 ? 's' : ''} selecionado{selectedUsers.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
